import { delay, deferred, type Deferred } from '../util/promise.js';
import type { Transport, TransportOutcome } from './transport.js';
import type { FlushResult, LogPayload, ResolvedLoggerOptions } from '../types.js';

interface QueueItem {
  payload: LogPayload;
  attempts: number;
  authRetried: boolean;
}

export interface PayloadQueue {
  enqueue(payload: LogPayload): void;
  flush(timeoutMs?: number): Promise<FlushResult>;
  drain(): { items: LogPayload[]; counters: FlushResult };
  close(): Promise<void>;
  size(): number;
  inFlight(): number;
  counters(): FlushResult;
}

export function createPayloadQueue(
  options: ResolvedLoggerOptions,
  transport: Transport,
  authRefresh?: () => Promise<void>,
): PayloadQueue {
  const items: QueueItem[] = [];
  let inFlightCount = 0;
  let closed = false;
  const sent = { count: 0 };
  const dropped = { count: 0 };
  const retried = { count: 0 };

  const idle: Deferred<void>[] = [];

  function notifyIdle(): void {
    if (items.length === 0 && inFlightCount === 0) {
      while (idle.length > 0) {
        idle.shift()!.resolve();
      }
    }
  }

  function reportError(err: unknown, payload?: LogPayload): void {
    try {
      options.onError(err, payload);
    } catch {
      /* swallow */
    }
  }

  function pump(): void {
    if (closed) return;
    while (inFlightCount < options.maxConcurrent && items.length > 0) {
      const item = items.shift();
      if (!item) break;
      inFlightCount++;
      processItem(item).finally(() => {
        inFlightCount--;
        notifyIdle();
        pump();
      });
    }
  }

  async function handleOutcome(item: QueueItem, outcome: TransportOutcome): Promise<void> {
    if (outcome.kind === 'ok') {
      sent.count++;
      return;
    }
    if (outcome.kind === 'auth-error') {
      if (item.authRetried || !authRefresh) {
        dropped.count++;
        reportError(new Error(`auth error (HTTP ${outcome.status})`), item.payload);
        return;
      }
      item.authRetried = true;
      try {
        await authRefresh();
      } catch (err) {
        reportError(err, item.payload);
      }
      const next = await transport.send(item.payload);
      return handleOutcome(item, next);
    }
    if (outcome.kind === 'fatal') {
      dropped.count++;
      reportError(
        new Error(`fatal HTTP ${outcome.status}`),
        item.payload,
      );
      return;
    }
    if (item.attempts >= options.maxRetries) {
      dropped.count++;
      reportError(
        new Error(`exhausted ${options.maxRetries} retries (last status ${outcome.status})`),
        item.payload,
      );
      return;
    }
    item.attempts++;
    retried.count++;
    const wait = options.retryBaseDelayMs * Math.pow(2, item.attempts - 1);
    await delay(wait);
    if (closed) return;
    const next = await transport.send(item.payload);
    return handleOutcome(item, next);
  }

  async function processItem(item: QueueItem): Promise<void> {
    try {
      const outcome = await transport.send(item.payload);
      await handleOutcome(item, outcome);
    } catch (err) {
      dropped.count++;
      reportError(err, item.payload);
    }
  }

  return {
    enqueue(payload) {
      if (closed) return;
      if (items.length >= options.maxQueueSize) {
        items.shift();
        dropped.count++;
      }
      items.push({ payload, attempts: 0, authRetried: false });
      pump();
    },

    async flush(timeoutMs) {
      pump();
      if (items.length === 0 && inFlightCount === 0) {
        return {
          sent: sent.count,
          dropped: dropped.count,
          retried: retried.count,
        };
      }
      const d = deferred<void>();
      idle.push(d);
      const wait = timeoutMs && timeoutMs > 0 ? timeoutMs : 0;
      if (wait > 0) {
        let timedOut = false;
        const t = setTimeout(() => {
          timedOut = true;
          d.resolve();
        }, wait);
        await d.promise;
        clearTimeout(t);
        if (timedOut) {
          // surface partial counters anyway
        }
      } else {
        await d.promise;
      }
      return {
        sent: sent.count,
        dropped: dropped.count,
        retried: retried.count,
      };
    },

    drain() {
      const out = items.map((i) => i.payload);
      items.length = 0;
      const counters = {
        sent: sent.count,
        dropped: dropped.count,
        retried: retried.count,
      };
      return { items: out, counters };
    },

    async close() {
      closed = true;
      items.length = 0;
      while (idle.length > 0) idle.shift()!.resolve();
    },

    size: () => items.length,
    inFlight: () => inFlightCount,
    counters: () => ({
      sent: sent.count,
      dropped: dropped.count,
      retried: retried.count,
    }),
  };
}
